import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UserX, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtError } from "@/lib/api";

/** Safety & privacy → Blocked users list (unblock inline). */
export default function SettingsBlocked() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/blocked")
      .then(({ data }) => setBlocked(Array.isArray(data) ? data : []))
      .catch((e) => {
        setBlocked([]);
        toast.error(fmtError(e.response?.data?.detail) || e.message);
      })
      .finally(() => setLoading(false));
  }, []);

  const unblock = async (uid) => {
    setBusyId(uid);
    try {
      await api.delete(`/blocked/${uid}`);
      setBlocked((arr) => arr.filter((u) => u.id !== uid));
      toast.success(t("settings.unblocked"));
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-blocked-page">
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="blocked-back" aria-label="Back">
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[28px] tracking-tight">{t("settings.blockedUsers")}</h1>
        </div>
      </div>

      <section className="px-5 pt-4">
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-[#7C828D]" /></div>
        ) : blocked.length === 0 ? (
          <p className="text-sm text-[#7C828D] py-3">{t("settings.noBlocked")}</p>
        ) : (
          <ul className="divide-y divide-[#F0EDE3]">
            {blocked.map((u) => (
              <li key={u.id} className="flex items-center gap-3 py-3" data-testid={`blocked-${u.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#F4F1E8] overflow-hidden flex items-center justify-center font-bold">
                  {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : (u.name?.[0] || "?").toUpperCase()}
                </div>
                <span className="flex-1 font-semibold truncate">{u.name || "User"}</span>
                <button
                  onClick={() => unblock(u.id)}
                  disabled={busyId === u.id}
                  className="bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold text-sm px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                  data-testid={`unblock-${u.id}`}
                >
                  {busyId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserX className="w-3.5 h-3.5" strokeWidth={3} />}
                  {t("settings.unblock")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
